import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from 'axios';
import * as React from 'react';

import { FetchError } from '../classes/FetchError';
import { BaseApiResponseState } from '../interfaces/BaseAPIResponseState';
import { loginRedirect } from '../utils/loginRedirect';

/**
 * checks that your deserializer takes an axios response and returns
 * a serialized dto
 */
type ResponseDeserializer<AxiosResponse, ReturnTypeFromJSONMethod> = (
  args: AxiosResponse,
) => ReturnTypeFromJSONMethod;

type GenericUnknownResponseObject = Record<string, unknown>;
type UseAxiosParams<AxiosResponse, DataStateResponse> = {
  /**
   * makes sure your serializer matches the axiosResponse
   * and returning the data you expect
   */
  deserializer?: ResponseDeserializer<AxiosResponse, DataStateResponse>;
  axiosInstance?: AxiosInstance;
};

export interface AxiosDataState<Data = unknown>
  extends BaseApiResponseState<Data> {
  resetState: () => void;
}

export type AxiosRequestFunction = (options: AxiosConfig) => Promise<void>;

type UseAxios = <
  DataStateResponse extends {
    [K in keyof DataStateResponse]: DataStateResponse[K];
  },
  AxiosResponse extends GenericUnknownResponseObject = DataStateResponse,
>(
  options?: UseAxiosParams<AxiosResponse, DataStateResponse>,
) => [
  AxiosDataState<
    ModelUseAxiosResponse<
      AxiosResponse,
      DataStateResponse,
      UseAxiosParams<AxiosResponse, DataStateResponse>['deserializer']
    >
  >,
  AxiosRequestFunction,
];

export type AxiosConfig = Omit<AxiosRequestConfig, 'method'> & {
  // discourages use of GET in favor or useFetch hook
  method: Exclude<Method, 'get' | 'GET'>;
};

type ModelUseAxiosResponse<
  AxiosResponse,
  DataStateResponse,
  Deserializer extends
    | ResponseDeserializer<AxiosResponse, DataStateResponse>
    | undefined,
> = Deserializer extends undefined ? AxiosResponse : DataStateResponse;

/**
 * provides an API similar to useFetch to handle all other types of requests
 * like POST, PUT and other types of requests. If doing a GET request, please use
 * the useFetch hook.
 *
 * @see {@link useFetch useFetch hook}
 * @see {@link https://github.com/project44/micro-webapps/wiki/Sharing-Models-Between-the-Gateways-and-UIs#fetching-data-from-the-gateway-in-the-ui-get-requests Fetching data from the gateway in the UI (GET requests)}
 *
 * @todo When axios gets updated to v.22, implement the abortController
 * to enable canceling the in-flight requests feature. Given how close axios is to v.22, and how
 * the CancelToken has been deprecated, it's best to wait.
 *
 * @see {@link https://github.com/axios/axios#abortcontroller Canceling axios request using Abort Controller (not implemented yet)}.
 * @see {@link https://github.com/axios/axios#canceltoken-deprecated CancelToken (DEPRECATED)}.
 */
export const useAxios: UseAxios = <DataStateResponse, ResponseInterface>(
  options?: UseAxiosParams<ResponseInterface, DataStateResponse>,
) => {
  const { deserializer, axiosInstance } = options || {};
  const axiosRequestFunction = axiosInstance ? axiosInstance : axios;

  const [data, setData] = React.useState<
    | ModelUseAxiosResponse<
        ResponseInterface,
        DataStateResponse,
        typeof deserializer
      >
    | undefined
  >(undefined);

  const [error, setError] = React.useState<undefined | FetchError>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  /**
   * for cleanup functions or for simply 'resetting state' on the api call
   */
  const resetState: () => void = React.useCallback(() => {
    setIsLoading(false);
    setData(undefined);
    setError(undefined);
  }, []);

  const axiosRequest: AxiosRequestFunction = React.useCallback(
    async (axiosRequestConfig) => {
      const configMethod = axiosRequestConfig?.method;

      const axiosRequestMethod: Method =
        /**
         * toLowerCase() does not take a Generic parameter
         * toLowerCase<Method>() so asserting to one of the methods allowed
         * as noted on AxiosRequestConfig['method']
         */
        <Method>configMethod.toLowerCase();

      try {
        setIsLoading(true);
        setData(undefined);
        setError(undefined);

        /**
         * Throw an error if developer is doing a GET request since they should
         * be using the useFetch hook; both 'GET' and 'get' are valid
         * axios method properties.
         */
        if (axiosRequestMethod === 'get') {
          throw new Error('Please use useFetch hook for GET api calls');
        }

        const { data: responseData }: AxiosResponse<ResponseInterface> =
          await axiosRequestFunction(axiosRequestConfig);

        const modeledData = deserializer
          ? deserializer(responseData)
          : responseData;

        setData(modeledData);
      } catch (e: unknown) {
        /**
         * Same as useFetch, if response.status is 401, redirect
         * to login page
         */
        const axiosError = e as AxiosError;
        if (
          axiosError?.response?.status === 401 &&
          window.location.pathname !== '/login'
        ) {
          return loginRedirect();
        }

        if (axiosError?.isAxiosError) {
          const fetchError = new FetchError(
            axiosError?.response?.statusText || 'Fetch error',
            axiosError?.response?.status,
            axiosError.response?.data,
          );

          setError(fetchError);
          return;
        }

        setError(new FetchError(axiosError.message));
        return;
      } finally {
        setIsLoading(false);
      }
    },
    [axiosRequestFunction, deserializer],
  );

  /**
   * Memoizing to avoid recomputing
   * the return value/overall useAxios state if a rerender happens
   */
  const memoizedState = React.useMemo(() => {
    return { data, error, isLoading, resetState };
  }, [data, error, isLoading, resetState]);

  React.useEffect(() => {
    /**
     * run a cleanup function to avoid unwanted behaviors
     * like memory leaks.
     * @see {@link https://reactjs.org/docs/hooks-effect.html#effects-with-cleanup Effects with Cleanup}
     */
    return () => resetState();
  }, [resetState]);

  return [memoizedState, axiosRequest];
};
